import React, { useEffect } from "react";
import { useTitle } from "../provider/TitleProvider";

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div>
    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">{title}</h3>
    <div className="space-y-3 text-sm text-gray-600 leading-relaxed">{children}</div>
  </div>
);

const TermsOfUse = () => {
  const { setTitle } = useTitle();

  useEffect(() => {
    setTitle("Terms of Use");
  }, []);

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm space-y-8">
        <div className="space-y-3 text-sm text-gray-600 leading-relaxed">
          <p>
            Thank you for visiting our website. This website is owned and operated by Fintree Holding Group Pty Ltd
            Trading as Buysooner (ABN 663 384 889).
          </p>
          <p>
            By accessing and/or using this website and related services, you agree to these{" "}
            <em>
              <strong>Terms of Use</strong>
            </em>{" "}
            (Terms) and our Privacy Policy. You should review our Privacy Policy and these Terms carefully and
            immediately cease using our website if you do not agree to them. In these Terms, 'us', 'we' and 'our' means
            Fintree Holding Group Pty Ltd and our related bodies corporate.
          </p>
          <p>
            If you become a customer or accredited partner, separate terms and conditions will apply to your
            relationship with us.
          </p>
        </div>

        <div className="border-t border-gray-200" />

        <Section title="1. Registration">
          <p>You may need to be a registered member to access certain features of our website.</p>
          <p>
            When you register as a prospective customer and activate your account, you will provide us with personal
            information such as your name, email address and telephone number. You must ensure that this information is
            accurate and current. We will handle all personal information we collect in accordance with our Privacy
            Policy.
          </p>
          <p>
            When you register as a customer and activate your account, we will provide you with a username and password.
            You are responsible for keeping this username and password secure and are responsible for all use and
            activity carried out under this username.
          </p>
          <p>
            Registration as a customer requires you to be at least 18 years of age; possess the legal right and ability
            to enter into a legally binding agreement with us; and that you agree and warrant to use the website in
            accordance with these Terms.
          </p>
        </Section>

        <Section title="2. Collection Notice">
          <p>
            We collect personal information about you in order to process your registration, assess if you are approved
            for a Buysooner product and for purposes otherwise set out in our Privacy Policy.
          </p>
          <p>
            We may disclose that information to third parties that help us deliver our services (including information
            technology suppliers, communication suppliers and our business partners) or as required by law.
          </p>
        </Section>

        <Section title="3. Using our Website and Cookies">
          <p>
            We may use 'cookies' or other similar tracking technologies on our website that help us track your website
            usage and remember your preferences. Cookies are small files that store information on your computer, TV,
            mobile phone or other electronic device.
          </p>
          <p>
            You can disable cookies through your internet browser but our websites may not work as intended for you if
            you do so. We may also use cookies to enable us to collect data that may include personal information.
          </p>
        </Section>

        <Section title="4. Accuracy, Completeness and Timeliness of Information">
          <p>
            The information on our website is not comprehensive and is intended to provide a summary of the subject
            matter covered. While we use all reasonable attempts to ensure the accuracy and completeness of the
            information on our website, except to the extent required by law, we make no warranty and have no liability
            regarding the information on this website.
          </p>
          <p>
            We are not liable to you or anyone else if interference with or damage to your computer systems occurs in
            connection with the use of this website or a linked website. You must take your own precautions to ensure
            that whatever you select for your use from our website is free of viruses or anything else that may
            interfere with or damage the operations of your computer systems.
          </p>
        </Section>

        <Section title="5. Linked Sites">
          <p>
            Our website may contain links to websites operated by third parties. Those links are provided for
            convenience and may not remain current or be maintained. Unless expressly stated otherwise, we do not
            endorse and are not responsible for the content on those linked websites and have no control over or rights
            in those linked websites.
          </p>
        </Section>

        <Section title="6. Intellectual Property Rights">
          <p>
            Unless otherwise indicated, we own or license from third parties all rights, title and interest (including
            copyright, designs, patents, trademarks and other intellectual property rights) in this website and in all
            of the material made available on this website (Content).
          </p>
          <p>
            Any reproduction or redistribution of this website or the Content is prohibited and may result in civil and
            criminal penalties. You are permitted to make a copy for the purpose of viewing the Content for your own
            personal use.
          </p>
        </Section>

        <Section title="7. Unacceptable Activity">
          <p>
            You must not commit any act which is inappropriate, unlawful or prohibited by any laws applicable to our
            website, including but not limited to:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              Any act that would constitute a breach of either the privacy of any individual or any other of the legal
              rights of individuals;
            </li>
            <li>Any use of this website to defame or libel us, our employees or others;</li>
            <li>
              Any uploading of files that contain viruses that may cause damage to our property or the property of other
              individuals;
            </li>
            <li>
              Any posting or transmitting to this website of any non-authorised material including, but not limited to,
              material that is likely to cause annoyance, or which is defamatory, racist, obscene, threatening,
              pornographic or otherwise detrimental to or in violation of our systems or a third party's systems or
              network security.
            </li>
          </ul>
          <p>
            If we allow you to post any information to our website, we have the right to take down this information at
            our sole discretion and without notice.
          </p>
        </Section>

        <Section title="8. Warranties and Disclaimers">
          <p>
            To the maximum extent permitted by law, including the Australian Consumer Law, we make no warranties or
            representations about this website or the Content, including but not limited to warranties or
            representations that they will be complete, accurate or up-to-date, that access will be uninterrupted or
            error-free or free from viruses, or that this website will be secure.
          </p>
          <p>
            We reserve the right to restrict, suspend or terminate without notice your access to this website, any
            Content, or any feature of this website at any time without notice.
          </p>
        </Section>

        <Section title="9. Liability">
          <p>
            To the maximum extent permitted by law, including the Australian Consumer Law, in no event shall we be
            liable for any direct and indirect loss, damage or expense – irrespective of the manner in which it occurs –
            which may be suffered due to your use of our website and/or the information or materials contained on it, or
            as a result of the inaccessibility of this website and/or the fact that certain information or materials
            contained on it are incorrect, incomplete or not up-to-date.
          </p>
        </Section>

        <Section title="10. Jurisdiction and Governing Law">
          <p>
            Your use of the website and these Terms are governed by the law of New South Wales and you submit to the
            non-exclusive jurisdiction of the courts exercising jurisdiction in New South Wales.
          </p>
        </Section>
      </div>
    </div>
  );
};

export default TermsOfUse;
